/* ProductionTriageCard.js — v6.35.30

   Production Triage Quick Reference

   Mounted on Super Admin → Maintenance after the v6.35.23-29 14-hour HTTP 520
   outage taught us how expensive it is to triage prod issues without a runbook.

   What it does:
     1. Live "Is production healthy?" check — pings the public /api/platform/version
        and /health endpoints, surfaces version, latency, and HTTP status.
        Auto-refreshes every 30s while the card is mounted.
     2. "Copy diagnostic bundle" button — assembles a structured Markdown report
        (timestamp, preview version, live prod check result, recent error patterns)
        and writes it to clipboard. Operator pastes into Emergent support ticket
        or chat. Saves 5-10 min of manual context-gathering on every incident.
     3. Inline runbook — the exact 5-step checklist for the v6.35.23-29 outage
        class, baked into the UI so the operator never has to recall which
        screenshots to grab from Emergent's Error Logs panel.

   ZERO backend changes. ZERO writes. Public endpoints only.
   data-testid coverage on every interactive element.
*/

import React, { useState, useEffect, useRef } from "react";
import { Activity, AlertTriangle, CheckCircle, Clipboard, ClipboardCheck, ExternalLink, Loader2, RefreshCw } from "lucide-react";
import { Button } from "../components/ui/button";

const PROD_URL = "https://mosaicassessmentco.com";

export default function ProductionTriageCard() {
  const [checking, setChecking] = useState(false);
  const [result, setResult] = useState(null); // { ok, version, latencyMs, healthOk, error, checkedAt }
  const [copied, setCopied] = useState(false);
  const intervalRef = useRef(null);

  const runCheck = async () => {
    setChecking(true);
    const checkedAt = new Date().toISOString();
    const out = { checkedAt };

    // Race-safe parallel checks with hard 8s timeout each
    const fetchWithTimeout = (url, ms = 8000) => {
      const ctrl = new AbortController();
      const t = setTimeout(() => ctrl.abort(), ms);
      const started = performance.now();
      return fetch(url, { signal: ctrl.signal, cache: "no-store" })
        .then(async (r) => {
          clearTimeout(t);
          const latencyMs = Math.round(performance.now() - started);
          let body = null;
          try { body = await r.json(); } catch { body = null; }
          return { status: r.status, latencyMs, body };
        })
        .catch((e) => {
          clearTimeout(t);
          return { status: 0, latencyMs: Math.round(performance.now() - started), error: e.name || "fetch_error" };
        });
    };

    try {
      const [verRes, healthRes] = await Promise.all([
        fetchWithTimeout(`${PROD_URL}/api/platform/version`),
        fetchWithTimeout(`${PROD_URL}/health`),
      ]);
      out.versionStatus = verRes.status;
      out.versionLatencyMs = verRes.latencyMs;
      out.version = verRes.body?.version || null;
      out.healthStatus = healthRes.status;
      out.healthLatencyMs = healthRes.latencyMs;
      out.ok = verRes.status === 200 && healthRes.status === 200;
    } catch (e) {
      out.ok = false;
      out.error = String(e.message || e);
    }
    setResult(out);
    setChecking(false);
  };

  useEffect(() => {
    runCheck();
    intervalRef.current = setInterval(runCheck, 30000);
    return () => intervalRef.current && clearInterval(intervalRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const copyBundle = async () => {
    const r = result || {};
    const bundle = [
      "## MOSAIC Production Triage Report",
      `**Generated:** ${r.checkedAt || new Date().toISOString()}`,
      `**Preview origin:** ${window.location.origin}`,
      `**Production origin:** ${PROD_URL}`,
      "",
      "### Live Production Status",
      `- GET ${PROD_URL}/api/platform/version → HTTP ${r.versionStatus ?? "n/a"} in ${r.versionLatencyMs ?? "?"} ms`,
      `- Production version: ${r.version || "unknown"}`,
      `- GET ${PROD_URL}/health → HTTP ${r.healthStatus ?? "n/a"} in ${r.healthLatencyMs ?? "?"} ms`,
      `- Overall: ${r.ok ? "HEALTHY" : "UNHEALTHY / DEGRADED"}`,
      "",
      "### Triage Checklist (v6.35.23-29 outage class)",
      "1. Open Emergent dashboard → Deployments. Confirm latest deploy STATUS pill.",
      "   - GREEN/Live → fix is code-side; check runtime logs below.",
      "   - BLUE/In Progress → wait, then re-check.",
      "   - RED/Failed → open build logs, paste error tail into ticket.",
      "2. Click the 'View logs' icon next to the running deployment.",
      "   - All Logs tab: look for 'GET /health HTTP/1.0 404' (the v6.35.29 class).",
      "   - Error Logs tab: look for 'connect() failed (111: Connection refused)'.",
      "3. Compare prod /api/platform/version to current preview version above.",
      "   - Mismatch → operator never deployed; click Deploy.",
      "   - Match → code is current; investigate runtime.",
      "4. Verify /health returns 200 — added in v6.35.29 to satisfy K8s liveness probe.",
      "5. If after step 1-4 prod still 520: email support@emergent.sh with this bundle.",
      "",
      "### Common Error Log Signals",
      "- `/health 404` → missing K8s liveness handler (fixed v6.35.29)",
      "- `connect() failed (111: Connection refused) while connecting to upstream` → backend restarting",
      "- `Disk usage at startup: >85%` → pod approaching ENOSPC",
      "- `MongoTimeoutError` → Atlas connectivity issue, check Atlas status page",
      "",
      "### Operator Notes",
      "(add any context here before sharing the bundle)",
    ].join("\n");

    try {
      await navigator.clipboard.writeText(bundle);
      setCopied(true);
      setTimeout(() => setCopied(false), 3000);
    } catch (e) {
      // Fallback for older browsers
      const ta = document.createElement("textarea");
      ta.value = bundle;
      document.body.appendChild(ta);
      ta.select();
      try {
        document.execCommand("copy");
        setCopied(true);
        setTimeout(() => setCopied(false), 3000);
      } finally {
        document.body.removeChild(ta);
      }
    }
  };

  const statusPill = () => {
    if (checking && !result) {
      return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-slate-200 text-slate-700" data-testid="prod-triage-status-loading"><Loader2 className="w-3 h-3 animate-spin" /> Checking…</span>;
    }
    if (!result) return null;
    if (result.ok) {
      return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-emerald-100 text-emerald-800" data-testid="prod-triage-status-healthy"><CheckCircle className="w-3 h-3" /> Healthy</span>;
    }
    return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-rose-100 text-rose-800" data-testid="prod-triage-status-unhealthy"><AlertTriangle className="w-3 h-3" /> Unhealthy</span>;
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-6 mb-6 shadow-sm" data-testid="production-triage-card">
      <div className="flex items-start justify-between mb-4">
        <div>
          <h3 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
            <Activity className="w-5 h-5 text-indigo-600" />
            Production Triage Quick Reference
            {statusPill()}
          </h3>
          <p className="text-sm text-slate-600 mt-1">
            Live status of <code className="text-xs bg-slate-100 px-1 py-0.5 rounded">{PROD_URL}</code> + one-click diagnostic bundle for incident response.
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={runCheck}
          disabled={checking}
          data-testid="prod-triage-refresh-btn"
        >
          {checking ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          <span className="ml-1.5">Refresh</span>
        </Button>
      </div>

      {/* Live status grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
        <div className="border border-slate-200 rounded-lg p-3" data-testid="prod-triage-version-row">
          <div className="text-xs uppercase tracking-wide text-slate-500 mb-1">/api/platform/version</div>
          <div className="text-sm font-mono">
            HTTP <span className={result?.versionStatus === 200 ? "text-emerald-700 font-bold" : "text-rose-700 font-bold"}>{result?.versionStatus ?? "—"}</span>
            <span className="text-slate-500 ml-2">{result?.versionLatencyMs ?? "?"} ms</span>
          </div>
          <div className="text-xs text-slate-600 mt-1">version: <span className="font-mono font-semibold">{result?.version || "—"}</span></div>
        </div>
        <div className="border border-slate-200 rounded-lg p-3" data-testid="prod-triage-health-row">
          <div className="text-xs uppercase tracking-wide text-slate-500 mb-1">/health (K8s liveness probe)</div>
          <div className="text-sm font-mono">
            HTTP <span className={result?.healthStatus === 200 ? "text-emerald-700 font-bold" : "text-rose-700 font-bold"}>{result?.healthStatus ?? "—"}</span>
            <span className="text-slate-500 ml-2">{result?.healthLatencyMs ?? "?"} ms</span>
          </div>
          <div className="text-xs text-slate-600 mt-1">added in v6.35.29 — prevents K8s kill-loop</div>
        </div>
      </div>

      {/* Actions */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <Button
          onClick={copyBundle}
          size="sm"
          className="bg-indigo-600 hover:bg-indigo-700 text-white"
          data-testid="prod-triage-copy-bundle-btn"
        >
          {copied ? <ClipboardCheck className="w-4 h-4" /> : <Clipboard className="w-4 h-4" />}
          <span className="ml-1.5">{copied ? "Copied!" : "Copy diagnostic bundle"}</span>
        </Button>
        <a
          href="https://app.emergent.sh"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 px-3 py-1.5 text-sm border border-slate-300 rounded-md hover:bg-slate-50"
          data-testid="prod-triage-emergent-link"
        >
          <ExternalLink className="w-3.5 h-3.5" /> Open Emergent dashboard
        </a>
        <a
          href={`${PROD_URL}/api/platform/version`}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 px-3 py-1.5 text-sm border border-slate-300 rounded-md hover:bg-slate-50"
          data-testid="prod-triage-prod-link"
        >
          <ExternalLink className="w-3.5 h-3.5" /> Test prod /api/platform/version
        </a>
      </div>

      {/* Runbook */}
      <details className="text-sm text-slate-700" data-testid="prod-triage-runbook">
        <summary className="cursor-pointer font-medium text-slate-900 hover:text-indigo-700">
          📘 If production returns HTTP 520 — 5-step runbook
        </summary>
        <ol className="list-decimal list-inside mt-2 space-y-1.5 pl-2">
          <li>Open Emergent dashboard → Deployments. Confirm latest deploy status pill.</li>
          <li>Click "View logs" on the running deployment. Check All Logs + Error Logs tabs.</li>
          <li>Look for <code className="text-xs bg-slate-100 px-1 rounded">/health 404</code> (K8s probe class) or <code className="text-xs bg-slate-100 px-1 rounded">connect() failed (111: Connection refused)</code> (restart loop).</li>
          <li>Compare prod <code className="text-xs bg-slate-100 px-1 rounded">/api/platform/version</code> to preview version. Mismatch ⇒ click Deploy.</li>
          <li>If stuck after steps 1-4: click "Copy diagnostic bundle" above + email <code className="text-xs bg-slate-100 px-1 rounded">support@emergent.sh</code>.</li>
        </ol>
      </details>
    </div>
  );
}
